/**
 * The task pocket's More details page (spec §6.5, “More details”): Source
 * and Schedule, the task's Runs, then the tasks this task blocks and the
 * Direct session — today's sections as pocket rows.
 */
import type { Task } from "@/api/tasks";
import { Icons, PocketRow, PocketSection } from "@/components/ui";
import { useT } from "@/i18n";
import { panelsTasksMessages } from "@/i18n/messages/panels-tasks";
import { useTasksStore } from "@/stores/tasks-store";
import { sourceLabelKey } from "./task-detail-config";
import { TaskDirectSessionSection } from "./task-direct-session-section";
import { TaskScheduleSection } from "./task-schedule-section";
import { TaskRunsSection } from "./task-runs-section";

const ROW_ICON = "h-4 w-4 text-muted";

export function TaskPocketDetails({
  projectId,
  task,
  onOpenTask,
}: {
  projectId: string;
  task: Task;
  /** Opens a task this one blocks, where the pocket sits. */
  onOpenTask: (taskId: string) => void;
}) {
  const t = useT(panelsTasksMessages);
  const refetchSignal = useTasksStore((s) => s.refetchSignal);
  const sourceKey = sourceLabelKey(task.source);

  return (
    <div data-testid="task-pocket-details">
      <PocketSection>
        {sourceKey ? (
          <PocketRow
            testId="task-pocket-row-source"
            leading={<Icons.Info className={ROW_ICON} aria-hidden="true" />}
            title={t("source")}
            trailing={
              <span className="px-2 text-appkit-body text-muted">
                {t(sourceKey)}
              </span>
            }
          />
        ) : null}
        <TaskScheduleSection projectId={projectId} task={task} />
      </PocketSection>
      <TaskRunsSection
        projectId={projectId}
        taskId={task.id}
        refreshSignal={refetchSignal}
      />
      {task.blocks_detail.length > 0 ? (
        <PocketSection title={t("blocks")} testId="task-pocket-blocks">
          {task.blocks_detail.map((dependency) => (
            <PocketRow
              key={dependency.id}
              testId={`task-pocket-row-blocks-${dependency.id}`}
              leading={<Icons.Link className={ROW_ICON} aria-hidden="true" />}
              title={dependency.title}
              secondLine={dependency.ticket_display}
              onSelect={() => onOpenTask(dependency.id)}
            />
          ))}
        </PocketSection>
      ) : null}
      <TaskDirectSessionSection projectId={projectId} task={task} />
    </div>
  );
}
